import { useState } from "react"
import { useGetPaymentsQuery, useGetPaymentStatsQuery } from "../features/admin/adminApi"
import { CreditCard, Search, Filter, TrendingUp, DollarSign, CheckCircle, XCircle } from "lucide-react"

export default function PaymentsPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [methodFilter, setMethodFilter] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")

  const { data, isLoading, error } = useGetPaymentsQuery({
    page,
    search: debouncedSearch,
    status: statusFilter,
    method: methodFilter,
    dateFrom,
    dateTo,
  })

  const { data: stats } = useGetPaymentStatsQuery({ dateFrom, dateTo })

  const handleSearch = (value: string) => {
    setSearch(value)
    const timer = setTimeout(() => setDebouncedSearch(value), 500)
    return () => clearTimeout(timer)
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      success: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
    }
    return styles[status as keyof typeof styles] || "bg-gray-100 text-gray-800"
  }

  const getMethodBadge = (method: string) => {
    const styles = {
      card: "bg-blue-100 text-blue-800",
      bank_transfer: "bg-purple-100 text-purple-800",
      wallet: "bg-orange-100 text-orange-800",
    }
    return styles[method as keyof typeof styles] || "bg-gray-100 text-gray-800"
  }

  const formatCurrency = (amount: number, currency: string = "NGN") => {
    const symbol = currency === "NGN" ? "₦" : currency
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard size={28} />
          Payments & Transactions
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Monitor all payments, track revenue, and analyze transaction trends
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats.totalRevenue)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Success Rate</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {stats.successRate}%
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Successful</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {stats.successfulPayments.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle size={24} className="text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {stats.failedPayments.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <XCircle size={24} className="text-red-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by reference..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Methods</option>
            <option value="card">Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="wallet">Wallet</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From Date"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To Date"
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {(statusFilter || methodFilter || dateFrom || dateTo || debouncedSearch) && (
          <div className="mt-3 flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <p className="text-sm text-gray-600">
              Active filters:
              {statusFilter && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">Status: {statusFilter}</span>}
              {methodFilter && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">Method: {methodFilter}</span>}
              {dateFrom && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">From: {dateFrom}</span>}
              {dateTo && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">To: {dateTo}</span>}
              {debouncedSearch && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">Search: {debouncedSearch}</span>}
            </p>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {data && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-900">
            Showing <strong>{data.payments.length}</strong> of <strong>{data.total}</strong> transactions
          </p>
        </div>
      )}

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-gray-500">Loading payments...</div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">Failed to load payments</div>
        ) : !data?.payments.length ? (
          <div className="p-12 text-center text-gray-500">
            <CreditCard size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No payments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Invoice/Order</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-mono text-xs text-gray-900">{payment.referenceId}</p>
                        {payment.authorizationCode && (
                          <p className="font-mono text-xs text-gray-400 mt-0.5">Auth: {payment.authorizationCode}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{payment.userName || "N/A"}</p>
                        {payment.userEmail && (
                          <p className="text-xs text-gray-500 mt-0.5">{payment.userEmail}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        {payment.invoiceNumber && (
                          <p className="text-xs text-gray-600">Invoice: {payment.invoiceNumber}</p>
                        )}
                        {payment.orderNumber && (
                          <p className="text-xs text-gray-600 mt-0.5">Order: {payment.orderNumber}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(payment.amount, payment.currency)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getMethodBadge(payment.method)}`}>
                        {payment.method.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs text-gray-600">
                          {payment.transactionDate
                            ? new Date(payment.transactionDate).toLocaleDateString()
                            : new Date(payment.createdAt).toLocaleDateString()
                          }
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {payment.transactionDate
                            ? new Date(payment.transactionDate).toLocaleTimeString()
                            : new Date(payment.createdAt).toLocaleTimeString()
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Page {page} of {data.totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Payment Methods Breakdown */}
      {stats && stats.paymentMethods.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Payment Methods Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.paymentMethods.map((method) => (
              <div key={method.method} className="border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-500 capitalize">{method.method.replace("_", " ")}</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(method.total)}</p>
                <p className="text-xs text-gray-500 mt-1">{method.count} transactions</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
